import { DayOfWeek, Schedule } from './chores';

/** The shortest interval a chore can be scheduled on, and the unit every longer one is a multiple
 * of. Sub-day intervals are not supported: `calculateStatus` compares whole calendar days, so a
 * chore due at 09:00 still reads DueToday at 20:00 and only turns over at midnight — which leaves
 * an hourly chore no way to reach Overdue at all. Hours were dropped from the interval input for
 * that reason (see lib/dateUtils.parseIntervalString). */
export const MIN_INTERVAL_MINUTES = 1440;

/** An interval as whole days, never fewer than one. Rounds rather than rejects, so a row stored
 * while hours were still offered (or one carried over from the legacy `interval_frequency`
 * column) still schedules sensibly instead of sitting permanently un-overdue. */
export function intervalDays(intervalMinutes: number): number {
  return Math.max(1, Math.round(intervalMinutes / MIN_INTERVAL_MINUTES));
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const ORDINAL_LABELS: Record<1 | 2 | 3 | 4 | -1, string> = {
  1: 'first', 2: 'second', 3: 'third', 4: 'fourth', [-1]: 'last',
};

export function formatSchedule(schedule: Schedule): string {
  switch (schedule.type) {
    case 'minutes': {
      const days = intervalDays(schedule.interval);
      return `Every ${days} day${days === 1 ? '' : 's'}`;
    }
    case 'weekly': {
      const dayNames = schedule.days.map(d => d.charAt(0).toUpperCase() + d.slice(1));
      const interval = schedule.interval ?? 1;
      const prefix = interval === 1 ? 'Weekly' : `Every ${interval} weeks`;
      return `${prefix} on ${dayNames.join(', ')}`;
    }
    case 'monthly': {
      const interval = schedule.interval ?? 1;
      const prefix = interval === 1 ? 'Monthly' : `Every ${interval} months`;
      return `${prefix} on the ${ordinal(schedule.dayOfMonth)}`;
    }
    case 'monthlyWeekday': {
      const interval = schedule.interval ?? 1;
      const prefix = interval === 1 ? 'Monthly' : `Every ${interval} months`;
      const dayName = schedule.weekday.charAt(0).toUpperCase() + schedule.weekday.slice(1);
      return `${prefix} on the ${ORDINAL_LABELS[schedule.ordinal]} ${dayName}`;
    }
    case 'yearly': {
      const monthName = new Date(2000, schedule.month - 1, 1).toLocaleString('default', { month: 'long' });
      return `Yearly on ${monthName} ${ordinal(schedule.dayOfMonth)}`;
    }
    case 'once':
      return `Once on ${new Date(schedule.dueDate + 'T00:00:00').toLocaleDateString()}`;
  }
}

const DAY_MAP: Record<DayOfWeek, number> = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
  thursday: 4, friday: 5, saturday: 6,
};

/** The common schedules, offered as one tap each in the chore form, with "Custom…" for the rest. */
export type SchedulePreset = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'halfYearly' | 'yearly' | 'once';

/** What a preset's next due date counts from: when the chore was last done (an interval of days),
 * or a fixed day on the calendar (today's weekday, date or day of the year). */
export type ScheduleAnchor = 'done' | 'fixed';

/** The days a preset spans when it counts from when the chore was done. A month is 30 days: since
 * the due date moves with each completion anyway, it never drifts from anything that matters. */
const PRESET_INTERVAL_DAYS: Partial<Record<SchedulePreset, number>> = {
  daily: 1,
  weekly: 7,
  monthly: 30,
  quarterly: 90,
  halfYearly: 180,
  yearly: 365,
};

export const SCHEDULE_PRESETS: { value: SchedulePreset; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Every 3 months' },
  { value: 'halfYearly', label: 'Every 6 months' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'once', label: 'Once' },
];

const WEEKDAYS: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/** A local date as YYYY-MM-DD; toISOString would give the UTC date, a day off near midnight. */
export function localIsoDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** The days a one-off is most often due, one tap each: today, tomorrow, the coming Saturday and
 * the coming Monday. Saturday and Monday are left out on the day before, where they'd be tomorrow
 * again; on the day itself they're a week on. */
export function oneOffQuickDates(today: Date = new Date()): { label: string; date: string }[] {
  const inDays = (n: number) => new Date(today.getFullYear(), today.getMonth(), today.getDate() + n);
  const daysUntil = (weekday: number) => ((weekday - today.getDay() + 6) % 7) + 1;
  const options = [
    { label: 'Today', offset: 0 },
    { label: 'Tomorrow', offset: 1 },
    { label: 'Saturday', offset: daysUntil(6) },
    { label: 'Next week', offset: daysUntil(1) },
  ].filter(({ offset }, i) => i < 2 || offset !== 1);
  return options.map(({ label, offset }) => ({ label, date: localIsoDate(inDays(offset)) }));
}

/** The schedule a preset stands for. Counted from when it's done, an interval of days; counted from
 * a fixed day, anchored on `today`: weekly on today's weekday, monthly on today's date, yearly on
 * today's day of the year. Daily is the same either way, and a one-off is due tomorrow. */
export function scheduleForPreset(preset: SchedulePreset, anchor: ScheduleAnchor, today: Date = new Date()): Schedule {
  const days = PRESET_INTERVAL_DAYS[preset];
  if (anchor === 'done' && days !== undefined) {
    return { type: 'minutes', interval: days * MIN_INTERVAL_MINUTES };
  }
  switch (preset) {
    case 'daily':
      return { type: 'minutes', interval: MIN_INTERVAL_MINUTES };
    case 'weekly':
      return { type: 'weekly', days: [WEEKDAYS[today.getDay()]] };
    case 'monthly':
      return { type: 'monthly', dayOfMonth: today.getDate() };
    case 'quarterly':
      return { type: 'monthly', dayOfMonth: today.getDate(), interval: 3 };
    case 'halfYearly':
      return { type: 'monthly', dayOfMonth: today.getDate(), interval: 6 };
    case 'yearly':
      return { type: 'yearly', month: today.getMonth() + 1, dayOfMonth: today.getDate() };
    case 'once': {
      const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
      return { type: 'once', dueDate: localIsoDate(tomorrow) };
    }
  }
}

/** Which preset a schedule is, whatever day it falls on, or null when only "Custom…" can show it
 * (several weekdays, every other week, the Nth weekday of the month, every 10 days…). */
export function presetOf(schedule: Schedule): SchedulePreset | null {
  switch (schedule.type) {
    case 'minutes': {
      const days = intervalDays(schedule.interval);
      const preset = (Object.keys(PRESET_INTERVAL_DAYS) as SchedulePreset[])
        .find(p => PRESET_INTERVAL_DAYS[p] === days);
      return preset ?? null;
    }
    case 'weekly':
      return schedule.days.length === 1 && (schedule.interval ?? 1) === 1 ? 'weekly' : null;
    case 'monthly': {
      const interval = schedule.interval ?? 1;
      if (interval === 1) return 'monthly';
      if (interval === 3) return 'quarterly';
      if (interval === 6) return 'halfYearly';
      return null;
    }
    case 'monthlyWeekday':
      return null;
    case 'yearly':
      return 'yearly';
    case 'once':
      return 'once';
  }
}

/** What a schedule counts from, or null where that makes no difference: daily, which is the same
 * either way, and a one-off. */
export function anchorOf(schedule: Schedule): ScheduleAnchor | null {
  switch (schedule.type) {
    case 'minutes':
      return intervalDays(schedule.interval) === 1 ? null : 'done';
    case 'once':
      return null;
    default:
      return 'fixed';
  }
}

export function calculateNextDueFromSchedule(schedule: Schedule, baseDate: Date): Date {
  switch (schedule.type) {
    case 'minutes': {
      // Whole calendar days rather than a fixed span of milliseconds, so an interval that crosses
      // a DST boundary still lands at the same time of day it started from.
      const next = new Date(baseDate);
      next.setDate(next.getDate() + intervalDays(schedule.interval));
      return next;
    }
    case 'weekly':
      return nextWeeklyOccurrence(baseDate, schedule.days, schedule.interval ?? 1);
    case 'monthly':
      return nextMonthlyOccurrence(baseDate, schedule.dayOfMonth, schedule.interval ?? 1);
    case 'monthlyWeekday':
      return nextMonthlyWeekdayOccurrence(baseDate, schedule.weekday, schedule.ordinal, schedule.interval ?? 1);
    case 'yearly':
      return nextYearlyOccurrence(baseDate, schedule.month, schedule.dayOfMonth);
    case 'once':
      return new Date(schedule.dueDate + 'T00:00:00');
  }
}

/** Days since the Monday that starts `date`'s week (Monday = 0 … Sunday = 6), matching the
 * Monday-first order the day picker shows. */
function mondayIndex(date: Date): number {
  return (date.getDay() + 6) % 7;
}

/** When a chore is due next after being completed at `completedAt`. Unlike
 * `calculateNextDueFromSchedule` from a chore's creation date, a weekly, monthly or yearly chore done
 * off its scheduled day first counts for the nearest scheduled day, so finishing it early doesn't
 * leave it due again straight away. */
export function calculateNextDueAfterCompletion(schedule: Schedule, completedAt: Date): Date {
  return calculateNextDueFromSchedule(schedule, scheduledDayCompletionCountsFor(schedule, completedAt));
}

/** The scheduled day a completion counts for: the completion's own day if it is scheduled, else
 * whichever scheduled day is closest to it, before or after (the earlier one on a tie). So a
 * Sunday chore done on Saturday counts for that Sunday instead of coming due again the next day,
 * while one done a day late still counts for the Sunday just missed. */
function scheduledDayCompletionCountsFor(schedule: Schedule, completedAt: Date): Date {
  switch (schedule.type) {
    case 'weekly':
      return nearestScheduledWeekday(completedAt, schedule.days);
    case 'monthly':
      return nearestOccurrence(completedAt, monthOffsets(completedAt).map(([year, month]) =>
        monthlyDayIn(year, month, schedule.dayOfMonth)));
    case 'monthlyWeekday':
      return nearestOccurrence(completedAt, monthOffsets(completedAt).map(([year, month]) =>
        monthlyWeekdayIn(year, month, schedule.weekday, schedule.ordinal)));
    case 'yearly':
      return nearestOccurrence(completedAt, [-1, 0, 1].map(offset =>
        monthlyDayIn(completedAt.getFullYear() + offset, schedule.month - 1, schedule.dayOfMonth)));
    default:
      return completedAt;
  }
}

function nearestScheduledWeekday(date: Date, days: DayOfWeek[]): Date {
  const targetDays = new Set(days.map(d => DAY_MAP[d]));
  const day = date.getDay();
  if (targetDays.size === 0 || targetDays.has(day)) return date;

  let daysBack = 1;
  while (!targetDays.has((day - daysBack + 7) % 7)) daysBack++;
  let daysForward = 1;
  while (!targetDays.has((day + daysForward) % 7)) daysForward++;

  const nearest = new Date(date);
  nearest.setDate(nearest.getDate() + (daysForward < daysBack ? daysForward : -daysBack));
  return nearest;
}

/** The month before, of, and after `date`, as [year, month] (the month may run past 0–11). */
function monthOffsets(date: Date): [number, number][] {
  return [-1, 0, 1].map(offset => [date.getFullYear(), date.getMonth() + offset]);
}

/** Whichever of the chronologically ordered `occurrences` lies closest to `date` in calendar
 * days, the earlier one on a tie. */
function nearestOccurrence(date: Date, occurrences: Date[]): Date {
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const distance = (occurrence: Date) => Math.abs(Math.round((occurrence.getTime() - day.getTime()) / 86_400_000));
  return occurrences.reduce((nearest, candidate) => distance(candidate) < distance(nearest) ? candidate : nearest);
}

/** The next scheduled day later in `afterDate`'s own (Monday-first) week, or else the first
 * scheduled day `intervalWeeks` weeks on — so an every-2-weeks chore counts its weeks from the
 * last completion and skips the week in between. With an interval of 1 that is simply the next
 * scheduled day. */
function nextWeeklyOccurrence(afterDate: Date, days: DayOfWeek[], intervalWeeks: number): Date {
  const targetIndexes = days.map(d => (DAY_MAP[d] + 6) % 7).sort((a, b) => a - b);
  const weekStart = new Date(afterDate);
  weekStart.setHours(0, 0, 0, 0);
  weekStart.setDate(weekStart.getDate() - mondayIndex(afterDate));

  const laterThisWeek = targetIndexes.find(i => i > mondayIndex(afterDate));
  const next = new Date(weekStart);
  if (laterThisWeek !== undefined) {
    next.setDate(next.getDate() + laterThisWeek);
  } else {
    next.setDate(next.getDate() + intervalWeeks * 7 + (targetIndexes[0] ?? 0));
  }
  return next;
}

function nextMonthlyOccurrence(afterDate: Date, dayOfMonth: number, intervalMonths: number): Date {
  return monthlyDayIn(afterDate.getFullYear(), afterDate.getMonth() + intervalMonths, dayOfMonth);
}

/** `dayOfMonth` in the given month (which may run past 0–11), clamped to the month's last day. */
function monthlyDayIn(year: number, month: number, dayOfMonth: number): Date {
  const maxDay = new Date(year, month + 1, 0).getDate();
  return new Date(year, month, Math.min(dayOfMonth, maxDay));
}

function nextMonthlyWeekdayOccurrence(
  afterDate: Date,
  weekday: DayOfWeek,
  ordinal: 1 | 2 | 3 | 4 | -1,
  intervalMonths: number
): Date {
  return monthlyWeekdayIn(afterDate.getFullYear(), afterDate.getMonth() + intervalMonths, weekday, ordinal);
}

/** The `ordinal` `weekday` of the given month (which may run past 0–11). */
function monthlyWeekdayIn(year: number, month: number, weekday: DayOfWeek, ordinal: 1 | 2 | 3 | 4 | -1): Date {
  const targetDay = DAY_MAP[weekday];
  if (ordinal === -1) {
    // Walk back from the last day of the month to the last matching weekday.
    const last = new Date(year, month + 1, 0);
    while (last.getDay() !== targetDay) last.setDate(last.getDate() - 1);
    return last;
  }
  // Walk forward from the 1st to the first matching weekday, then add whole weeks.
  const first = new Date(year, month, 1);
  while (first.getDay() !== targetDay) first.setDate(first.getDate() + 1);
  first.setDate(first.getDate() + (ordinal - 1) * 7);
  return first;
}

function nextYearlyOccurrence(afterDate: Date, month: number, dayOfMonth: number): Date {
  return monthlyDayIn(afterDate.getFullYear() + 1, month - 1, dayOfMonth);
}
